import { Check } from 'lucide-react';

import { cn } from '@/lib/cn';
import { formatDuration } from './tree';
import { SubtaskComposer } from './SubtaskComposer';
import { useTasks, useToggleDone } from './useTasks';
import type { Task } from './types';

interface Props {
  parent: Task;
  /** Opens a child in its own detail sheet. Omitted where there is nowhere to go. */
  onOpenTask?: (taskId: number) => void;
}

/**
 * Direct children of a task, plus the one-line composer.
 *
 * Direct children only: a subtask's own subtasks belong on that subtask, and
 * flattening the branch here would turn a detail sheet into a second backlog.
 * The parent's rollups already say how big the branch is.
 */
export function SubtaskList({ parent, onOpenTask }: Props) {
  const children = useTasks({ parent_task_id: parent.id, include_closed: true });
  const toggleDone = useToggleDone();
  const rows = children.data ?? [];

  return (
    <div className="flex flex-col gap-2">
      {rows.length > 0 && (
        <ul className="divide-y divide-border rounded-lg border border-border">
          {rows.map((child) => {
            const isDone = child.status === 'done';
            const estimate = formatDuration(child.estimate_minutes);
            return (
              <li key={child.id} className="flex items-center gap-2 px-2 py-1.5">
                <button
                  type="button"
                  onClick={() => toggleDone(child)}
                  aria-pressed={isDone}
                  aria-label={
                    isDone ? `Mark "${child.title}" not done` : `Complete "${child.title}"`
                  }
                  className={cn(
                    'pressable flex size-check shrink-0 items-center justify-center rounded-sm border',
                    isDone
                      ? 'border-success bg-success text-text-on-accent'
                      : 'border-border-strong hover:border-accent',
                  )}
                >
                  {isDone && <Check size={12} strokeWidth={3} aria-hidden />}
                </button>

                {onOpenTask ? (
                  <button
                    type="button"
                    onClick={() => onOpenTask(child.id)}
                    title={child.title}
                    className={cn(
                      'min-w-0 flex-1 truncate rounded text-left text-small',
                      isDone ? 'text-text-tertiary line-through' : 'text-text',
                    )}
                  >
                    {child.title}
                  </button>
                ) : (
                  <span
                    title={child.title}
                    className={cn(
                      'min-w-0 flex-1 truncate text-small',
                      isDone ? 'text-text-tertiary line-through' : 'text-text',
                    )}
                  >
                    {child.title}
                  </span>
                )}

                {child.rollup_total_count > 1 && (
                  <span className="shrink-0 tabular text-label text-text-tertiary">
                    {child.rollup_done_count}/{child.rollup_total_count}
                  </span>
                )}

                <span className="w-[3rem] shrink-0 text-right text-label tabular text-text-tertiary">
                  {estimate ?? ''}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      <SubtaskComposer parentId={parent.id} />
    </div>
  );
}
